import React, {useCallback} from 'react';
import {useStartScreenUtil} from "src/components/start-workflow-instance/start-screen-util-hook-v1";
import {Alert, Box, Button, Form as AWSUIForm, Container, Spinner} from "@amzn/awsui-components-react-v3";
import Header from "@amzn/awsui-components-react-v3/polaris/header";
import {WorkflowCapabilityMetadata} from "src/components/start-workflow-instance/start-screen-components";
import {TabInputForm} from "src/components/common/tab-input-form";
import {DomainTracker} from "src/model/DomainTracker";
import {SaveWorkflowInputTemplateRequest} from '@amzn/sm-workflow-orchestration-service-js-client/lib/smworkfloworchestrationservicelambda';
import {useMutation} from "@tanstack/react-query";

export const SaveWorkflowInputTemplate = () => {
    /* istanbul ignore next */
    const mutation = useMutation({
        mutationFn: (saveWorkflowInputTemplateRequest: SaveWorkflowInputTemplateRequest) => {
            return new DomainTracker().saveWorkflowInputTemplate(saveWorkflowInputTemplateRequest)
        }
    });
    const {
        schemaLoading,
        startWFLoading,
        workflowDetails,
        enableLiveValidate,
        workflowInput,
        capabilitySelectedOption,
        capabilityOptions,
        primitiveSelectedOption,
        primitiveOptions,
        onCapabilityChangeHandler,
        onPrimitiveChangeHandler,
        workflowDefinitionOption,
        workflowDefinitionOptions,
        onWorkflowDefinitionChangeHandler,
        workflowUISchema,
        onTabFormDataChange,
        onTabFormDataError,
        errorText,
        itemsRef,
        onTabDataChangeHandler
    } = useStartScreenUtil();

    const handleFormSubmit = () => {
        const workflowInputTemplateId = `${capabilitySelectedOption.value}-${primitiveSelectedOption.value}-${workflowDefinitionOption.value}`;

        const saveWorkflowInputTemplateRequest: SaveWorkflowInputTemplateRequest = {
            workflowInputTemplateId: workflowInputTemplateId,
            defaultInputJSON: JSON.stringify(workflowInput),
        }
        mutation.mutate(saveWorkflowInputTemplateRequest);
    }

    const onTabFormDataSubmit = useCallback((tabId: string, tabIndex: number) => {
        onTabDataChangeHandler(tabId, tabIndex, () => {});
    }, [onTabDataChangeHandler])

    return (
        <>
            <Box padding={{bottom: "l"}}>
                <Alert visible={mutation?.isSuccess}
                       data-testid={"save-workflow-config-success-alert"}
                       type={"success"}
                       dismissible={true}
                       onDismiss={() => mutation.reset()}>
                    <p>Successfully saved workflow config defaults.</p>
                </Alert>
                <Alert visible={Boolean(mutation?.error)}
                       data-testid={"save-workflow-config-error-alert"}
                       type={"error"}
                       dismissible={true}
                       onDismiss={() => mutation.reset()}>
                    <p>Failed to save workflow config defaults.</p>
                </Alert>
            </Box>
            <AWSUIForm
                errorText={errorText}
                actions={
                    <Button variant="primary"
                            data-testid={"save-workflow-config-submit-button"}
                            disabled={
                                schemaLoading ||
                                mutation.isLoading ||
                                capabilitySelectedOption?.value === "" ||
                                primitiveSelectedOption?.value === "" ||
                                workflowDefinitionOption?.value === ""
                            }
                            loading={startWFLoading || mutation.isLoading}
                            onClick={() => handleFormSubmit()}>Save Workflow Config</Button>

                }
            >
                <Container header={<Header variant="h2">Save Workflow Config</Header>}>
                    <WorkflowCapabilityMetadata
                        capabilitySelectedOption={capabilitySelectedOption}
                        capabilityOptions={capabilityOptions}
                        primitiveSelectedOption={primitiveSelectedOption}
                        primitiveOptions={primitiveOptions}
                        onCapabilityChangeHandler={onCapabilityChangeHandler}
                        onPrimitiveChangeHandler={onPrimitiveChangeHandler}
                        workflowDefinitionOption={workflowDefinitionOption}
                        workflowDefinitionOptions={workflowDefinitionOptions}
                        onWorkflowDefinitionChangeHandler={onWorkflowDefinitionChangeHandler}
                        startWFLoading={startWFLoading}
                        schemaLoading={schemaLoading}
                    />
                </Container>
                <Container
                    data-testid={"workflow-input-rjsf-form-field"}
                    header={<Header variant={"h3"}>Workflow Input</Header>}
                >
                    {!capabilitySelectedOption.value ? (
                        <Alert>Please select capability to provide workflow input</Alert>
                    ) : !primitiveSelectedOption.value ? (
                        <Alert>Please select primitive to provide workflow input</Alert>
                    ) : !workflowDefinitionOption.value ? (
                        <Alert>Please select workflow definition to provide workflow input</Alert>
                    ) : (
                        <>
                            {schemaLoading ? <><Spinner size={"big"}/> Loading Workflow input form... </> :
                                <TabInputForm
                                    stageInstanceInputSchemaMap={workflowDetails.stageInstanceInputSchemaMap || {}}
                                    formInputData={workflowInput}
                                    onTabFormChange={onTabFormDataChange}
                                    onTabFormSubmit={onTabFormDataSubmit}
                                    onTabFormError={onTabFormDataError}
                                    ref={itemsRef}
                                    enableLiveValidate={enableLiveValidate}
                                    stageInstanceUISchemaMap={workflowUISchema || {}}
                                    stageRenderingOrder={workflowDetails.stages || []}
                                />
                            }
                        </>
                    )}
                </Container>
            </AWSUIForm>
        </>
    )
}
